import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, ArrowLeft, Boxes, RotateCcw, Save, Trash2 } from 'lucide-react';
import { STATIONS, type AssetInput, type Station } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  useAsset,
  useAssignAsset,
  useCreateAsset,
  useDeleteAsset,
  useReturnAsset,
  useSetAssetStatus,
  useUnassignAsset,
  useUpdateAsset,
} from '../../hooks/useAssets';
import { Button } from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { PersonPicker, type PersonPick } from '../../components/person/PersonPicker';
import { ApiError } from '../../lib/api';
import { ASSET_CATEGORIES } from '../../lib/assetCategories';
import { AssetHistoryPanel } from './AssetHistoryPanel';
import { AssetMetadataFields } from './AssetMetadataFields';
import { Field } from './assetFormField';

interface AssetFormProps {
  mode: 'create' | 'edit';
}

export function AssetForm({ mode }: AssetFormProps) {
  const { id } = useParams<{ id?: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const editing = mode === 'edit' && id;
  const existing = useAsset(editing ? id! : null);

  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [station, setStation] = useState<Station>(
    (params.get('station') as Station | null) ?? 'IT',
  );
  const [category, setCategory] = useState('');
  const [syscode, setSyscode] = useState('');
  const [notes, setNotes] = useState('');
  const [meta, setMeta] = useState<Record<string, string>>({});
  const [assignee, setAssignee] = useState<PersonPick | null>(null);
  const [confirmOnArrival, setConfirmOnArrival] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Create starts "hydrated" (empty is final); edit flips true once the
  // existing asset's metadata loads, re-seeding the additional-info editor.
  const [metaHydrated, setMetaHydrated] = useState(mode === 'create');

  const create = useCreateAsset();
  const update = useUpdateAsset(id ?? '');
  const remove = useDeleteAsset();
  const setStatus = useSetAssetStatus();
  const assignMut = useAssignAsset();
  const unassignMut = useUnassignAsset();
  const returnMut = useReturnAsset();

  // Hydrate from server when editing
  useEffect(() => {
    if (!existing.data) return;
    setTag(existing.data.asset_tag);
    setDescription(existing.data.description);
    setStation(existing.data.station);
    setCategory(existing.data.category ?? '');
    setSyscode(existing.data.syscode ?? '');
    setNotes(existing.data.notes ?? '');
    setConfirmOnArrival(existing.data.confirm_on_arrival);
    const m: Record<string, string> = {};
    for (const [k, v] of Object.entries(existing.data.metadata ?? {})) {
      if (typeof v === 'string') m[k] = v;
    }
    setMeta(m);
    setMetaHydrated(true);
  }, [existing.data]);

  // Resolve the assignee's display name once when editing so the
  // PersonPicker shows their name rather than a UUID.
  useEffect(() => {
    const pid = existing.data?.assigned_to_person_id;
    if (!pid) {
      setAssignee(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/admin/people/${pid}`, { credentials: 'include' });
        if (!r.ok) return;
        const json: { person?: { id: string; full_name: string; email: string } } = await r.json();
        if (!cancelled && json.person) {
          setAssignee({
            id: json.person.id,
            full_name: json.person.full_name,
            email: json.person.email,
          });
        }
      } catch {
        // best-effort — the picker just shows empty if the lookup fails
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existing.data?.assigned_to_person_id]);

  const valid = useMemo(
    () => tag.trim().length > 0 && description.trim().length > 0,
    [tag, description],
  );

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please{' '}
        <Link to="/login" className="underline">
          sign in
        </Link>
        .
      </main>
    );
  }

  async function onSave() {
    const cleanedMeta: Record<string, string> = {};
    for (const [k, v] of Object.entries(meta)) {
      const t = v.trim();
      if (t.length > 0) cleanedMeta[k] = t;
    }
    const payload: AssetInput = {
      asset_tag: tag.trim(),
      description: description.trim(),
      station,
      category: category.trim() || null,
      syscode: syscode.trim() || null,
      metadata: cleanedMeta,
      notes: notes.trim() || null,
      confirm_on_arrival: confirmOnArrival,
    };
    try {
      let savedId: string;
      let savedTag: string;
      let savedStation: Station;
      let existingAssignee: string | null = null;

      if (mode === 'create') {
        const created = await create.mutateAsync(payload);
        savedId = created.id;
        savedTag = created.asset_tag;
        savedStation = created.station;
      } else {
        const updated = await update.mutateAsync(payload);
        savedId = updated.id;
        savedTag = updated.asset_tag;
        savedStation = updated.station;
        existingAssignee = existing.data?.assigned_to_person_id ?? null;
      }

      // Reconcile assignment as a second step. The server forbids
      // assignment changes while status='issued', so for issued assets
      // we skip silently and surface a hint in the toast.
      const wantedAssignee = assignee?.id ?? null;
      if (wantedAssignee !== existingAssignee) {
        const isIssued = existing.data?.status === 'issued';
        if (isIssued) {
          toast.info('Saved. Assignment unchanged: asset is already issued.');
        } else if (wantedAssignee) {
          await assignMut.mutateAsync({ id: savedId, person_id: wantedAssignee });
          toast.success(
            mode === 'create'
              ? `Created ${savedTag} and assigned to ${assignee?.full_name}`
              : `Saved ${savedTag} and assigned to ${assignee?.full_name}`,
          );
        } else {
          await unassignMut.mutateAsync(savedId);
          toast.success(`Saved ${savedTag} and cleared assignee`);
        }
      } else {
        toast.success(mode === 'create' ? `Created ${savedTag}` : `Saved ${savedTag}`);
      }

      navigate(`/admin/assets?station=${savedStation}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'save failed');
    }
  }

  async function onRetire() {
    if (!editing || !existing.data) return;
    try {
      await setStatus.mutateAsync({ id: id!, status: 'retired' });
      toast.success(`Retired ${existing.data.asset_tag}`);
      setRetireOpen(false);
      navigate('/admin/assets');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'retire failed');
    }
  }

  async function onReturn() {
    if (!editing || !existing.data) return;
    try {
      await returnMut.mutateAsync(id!);
      toast.success(`Returned ${existing.data.asset_tag} — now available`);
      setReturnOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'return failed');
    }
  }

  async function onRestore() {
    if (!editing) return;
    try {
      await setStatus.mutateAsync({ id: id!, status: 'available' });
      toast.success('Restored to available');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'restore failed');
    }
  }

  async function onDelete() {
    if (!editing || !existing.data) return;
    try {
      await remove.mutateAsync(id!);
      toast.success('Deleted');
      setDeleteOpen(false);
      navigate('/admin/assets');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'delete failed');
    }
  }

  const isCoord = user.roles.some((r) => ['admin', 'super_admin', 'coordinator'].includes(r));

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to="/admin/assets"
          className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to equipment
        </Link>

        <header className="mt-4 flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Boxes className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              {mode === 'create' ? 'New item' : 'Edit item'}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {mode === 'create' ? 'Register equipment' : tag || 'Item'}
            </h1>
            {existing.data ? (
              <p className="mt-1 text-xs text-fg-muted">
                Status: <span className="font-medium">{existing.data.status}</span>
              </p>
            ) : null}
          </div>
        </header>

        {existing.error ? (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{(existing.error as Error).message}</span>
          </div>
        ) : null}

        <div className="mt-6 space-y-4 rounded-xl border border-line/10 bg-surface p-5 shadow-sm">
          <p className="rounded-md bg-surface-2 px-3 py-2 text-[11px] text-fg-muted">
            This register is for individually-tracked kit (laptops, vans, tools). Uniform and PPE
            consumables aren't registered here — they're sized via the uniform sizing form and
            handled on the Kit to order screen.
          </p>

          <Field
            label="Item tag / ID"
            htmlFor="tag"
            required
            hint="Unique identifier — e.g. LT-COR-2999"
          >
            <input
              id="tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="LT-COR-2999"
              className="font-mono"
            />
          </Field>

          <Field label="Description" htmlFor="description" required>
            <input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Dell Latitude 5450 14"'
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Station" htmlFor="station" required>
              <select
                id="station"
                value={station}
                onChange={(e) => setStation(e.target.value as Station)}
              >
                {STATIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Category"
              htmlFor="category"
              hint="Pick from the list or type your own for one-offs."
            >
              <input
                id="category"
                list={`asset-categories-${station}`}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder={ASSET_CATEGORIES[station][0] ?? 'Laptop'}
                autoComplete="off"
              />
              {/* Native combobox suggestions, scoped per station so PPE
                  doesn't suggest "Laptop" and IT doesn't suggest
                  "Hi-vis". Free typing is still allowed. */}
              <datalist id={`asset-categories-${station}`}>
                {ASSET_CATEGORIES[station].map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </Field>
          </div>

          <AssetMetadataFields
            station={station}
            category={category}
            syscode={syscode}
            onSyscodeChange={setSyscode}
            meta={meta}
            setMeta={setMeta}
            hydrated={metaHydrated}
          />

          <div>
            <div className="mb-1 flex items-center justify-between gap-2 text-xs font-medium uppercase tracking-wider text-fg-muted">
              <span>Assigned to</span>
              {existing.data?.status === 'issued' ? (
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-success-soft/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success-soft-fg ring-1 ring-success-soft/25">
                    Issued — locked
                  </span>
                  {isCoord ? (
                    <button
                      type="button"
                      onClick={() => setReturnOpen(true)}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent transition-colors hover:bg-accent/10"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Return
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <PersonPicker
              value={assignee}
              onChange={setAssignee}
              placeholder="Unassigned (pick someone to pre-assign)"
              ariaLabel="Pre-assign to person"
              disabled={existing.data?.status === 'issued'}
            />
            <p className="mt-1 text-[11px] text-fg-muted">
              Setting a person pre-assigns this asset to them. On the day, the IT/Fleet station will
              surface it under their Equipment panel. An already-issued item must be returned before
              it can be reassigned — use <strong>Return</strong> above to free it (records a return
              on the audit trail).
            </p>
          </div>

          <label className="flex items-start gap-3 rounded-md border border-line/10 bg-surface-2 px-3 py-2.5">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={confirmOnArrival}
              onChange={(e) => setConfirmOnArrival(e.target.checked)}
            />
            <span className="text-sm">
              <span className="font-medium text-fg">Confirm on arrival (existing kit)</span>
              <span className="mt-0.5 block text-[11px] text-fg-muted">
                The assignee already holds this item (e.g. a TUPE transfer). On the day the station
                desk confirms possession and condition instead of issuing it.
              </span>
            </span>
          </label>

          <Field label="Notes" htmlFor="notes" hint="Optional, e.g. condition or history.">
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              size="md"
              iconLeft={<Save className="h-4 w-4" />}
              loading={create.isPending || update.isPending}
              disabled={!valid}
              onClick={onSave}
            >
              {mode === 'create' ? 'Create item' : 'Save changes'}
            </Button>
            <Link to="/admin/assets">
              <Button variant="ghost" size="md">
                Cancel
              </Button>
            </Link>
          </div>
          {editing && existing.data && isCoord ? (
            <div className="flex flex-wrap gap-2">
              {existing.data.status === 'retired' ? (
                <Button variant="secondary" size="md" onClick={onRestore}>
                  Restore
                </Button>
              ) : (
                <Button variant="ghost" size="md" onClick={() => setRetireOpen(true)}>
                  Retire
                </Button>
              )}
              <Button
                variant="ghost"
                size="md"
                iconLeft={<Trash2 className="h-4 w-4" />}
                onClick={() => setDeleteOpen(true)}
                className="text-danger-soft-fg hover:bg-danger-soft/15"
              >
                Delete
              </Button>
            </div>
          ) : null}
        </div>

        {editing ? <AssetHistoryPanel assetId={id!} /> : null}
      </div>

      <ConfirmModal
        open={retireOpen}
        tone="warning"
        title={`Retire ${existing.data?.asset_tag ?? 'item'}?`}
        message="The item hides from default lists. You can restore it later."
        confirmLabel="Retire"
        cancelLabel="Keep active"
        onConfirm={onRetire}
        onClose={() => setRetireOpen(false)}
      />

      <ConfirmModal
        open={returnOpen}
        tone="warning"
        title={`Return ${existing.data?.asset_tag ?? 'item'}?`}
        message={
          `This frees the item for reassignment and records a return against ` +
          `${existing.data?.assigned_to_full_name ?? 'the current holder'} on the audit trail. ` +
          `No signature is captured — use this to correct a mis-issue.`
        }
        confirmLabel="Return item"
        cancelLabel="Cancel"
        onConfirm={onReturn}
        onClose={() => setReturnOpen(false)}
      />

      <ConfirmModal
        open={deleteOpen}
        tone="destructive"
        title={`Permanently delete ${existing.data?.asset_tag ?? 'item'}?`}
        message="Only available, unassigned items can be deleted. The row goes for good; consider Retire instead if you want a paper trail."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={onDelete}
        onClose={() => setDeleteOpen(false)}
      />
    </main>
  );
}
